import React, { useEffect, useState, useRef } from "react";
import { AccountConsole, ConsoleActionGroup, LifecycleFilter } from "@/components/AccountConsole";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Search } from "lucide-react";
import { UserAccessActions } from "@/components/UserAccessActions";

const SEC_LABEL = { set_password: "Password impostata", change_email: "Email cambiata", send_password_reset: "Reset inviato", send_verification: "Verifica inviata", change_business: "Dati aziendali" };

export default function AdminUsers() {
  const { impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [lifecycle, setLifecycle] = useState("active");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(0);
  const [role, setRole] = useState("staff");
  const [q, setQ] = useState("");
  const [secLogs, setSecLogs] = useState([]);

  const load = (targetPage = page) => {
    const id = ++requestId.current; setLoading(true); setError("");
    api.get("/admin/profiles", { params: { role, q, lifecycle, page: typeof targetPage === "number" ? targetPage : page } })
      .then(({ data }) => { if (id === requestId.current) { setUsers(data.items); setTotal(data.total); } })
      .catch(() => { if (id === requestId.current) setError("Impossibile caricare gli utenti. Riprova con Cerca."); })
      .finally(() => { if (id === requestId.current) setLoading(false); });
  };
  useEffect(() => { setPage(1); load(1); /* eslint-disable-next-line */ }, [role, lifecycle]);
  useEffect(() => { api.get("/admin/security-logs").then((r) => setSecLogs(r.data || [])).catch(() => {}); }, []);

  const [showStaff, setShowStaff] = useState(false);
  const [staffForm, setStaffForm] = useState({ email: "", name: "", password: "", role: "support" });
  const [staffBusy, setStaffBusy] = useState(false);
  const createStaff = async () => {
    if (!staffForm.email.trim() || !staffForm.name.trim() || !staffForm.password.trim()) { toast.error("Compila tutti i campi"); return; }
    setStaffBusy(true);
    try { await api.post("/admin/staff", staffForm); toast.success(`Profilo ${staffForm.role === "support" ? "Assistenza" : "Agente"} creato`); setShowStaff(false); setStaffForm({ email: "", name: "", password: "", role: "support" }); setRole("staff"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setStaffBusy(false); }
  };

  const onImpersonate = async (u) => {
    try {
      await impersonate(u.id);
      toast.success(`Ora impersoni ${u.name}`);
      navigate("/app/dashboard");
    } catch (e) { toast.error(e.response?.data?.detail || "Impossibile impersonare"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Amministrazione</p>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Utenti interni</h1>
          <p className="text-sm text-muted-foreground mt-1">Staff interno: amministratori, assistenza tecnica e agenti commerciali.</p>
        </div>
        <Button onClick={() => setShowStaff(true)} data-testid="create-staff-open">+ Crea profilo staff</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Tabs value={role} onValueChange={setRole}>
          <TabsList className="h-auto flex-wrap justify-start">
            <TabsTrigger value="staff" data-testid="uf-staff">Tutti</TabsTrigger>
            <TabsTrigger value="admin" data-testid="uf-admin">Admin</TabsTrigger>
            <TabsTrigger value="support" data-testid="uf-support">Assistenza</TabsTrigger>
            <TabsTrigger value="agent" data-testid="uf-agent">Agenti</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { setPage(1); load(1); } }}
            placeholder="Cerca nome o email…" className="pl-9" data-testid="user-search-input" />
        </div>
        <Button variant="outline" onClick={() => { setPage(1); load(1); }} data-testid="user-search-button">Cerca</Button>
        <LifecycleFilter value={lifecycle} onChange={setLifecycle} />
      </div>

      <AccountConsole items={users} total={total} page={page} onPage={(value) => { setPage(value); load(value); }} onChanged={load} loading={loading} error={error} renderTools={(u) => (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid={`user-card-${u.id}`}>
          <ConsoleActionGroup title="Accesso" id={`staff-access-${u.id}`}><UserAccessActions user={u} mode="admin" onChanged={load} />{u.role !== "admin" && <Button size="sm" variant="outline" onClick={() => onImpersonate(u)} data-testid={`impersonate-${u.id}`}><LogIn className="w-4 h-4" /> Impersona</Button>}</ConsoleActionGroup>
        </div>
      )} />

      <Card className="p-5" data-testid="security-log-card">
        <h3 className="font-head font-semibold mb-1">Log di sicurezza</h3>
        <p className="text-xs text-muted-foreground mb-3">Modifiche email e reset/impostazione password: chi, su chi e quando.</p>
        <div className="space-y-2 max-h-80 overflow-auto">
          {secLogs.length === 0 && <p className="text-sm text-muted-foreground">Nessuna azione registrata.</p>}
          {secLogs.map((l) => (
            <div key={l.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm border-b border-border pb-1.5" data-testid={`security-log-${l.id}`}>
              <Badge variant="outline" className="font-mono text-[11px]">{SEC_LABEL[l.action] || l.action}</Badge>
              <span className="text-muted-foreground">{l.actor_email}</span>
              <span>→</span>
              <span className="font-medium">{l.target_email}</span>
              {l.meta?.old_email && <span className="text-xs text-muted-foreground">(era {l.meta.old_email})</span>}
              <span className="ml-auto text-xs text-muted-foreground">{new Date(l.at).toLocaleString("it-IT")}</span>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={showStaff} onOpenChange={setShowStaff}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>Crea profilo staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Ruolo</Label>
              <Select value={staffForm.role} onValueChange={(v) => setStaffForm({ ...staffForm, role: v })}>
                <SelectTrigger className="mt-1" data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="support">Assistenza tecnica</SelectItem><SelectItem value="agent">Commerciale / Agente</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Nome</Label><Input className="mt-1" value={staffForm.name} onChange={(e) => setStaffForm({ ...staffForm, name: e.target.value })} data-testid="staff-name" /></div>
            <div><Label>Email</Label><Input className="mt-1" type="email" value={staffForm.email} onChange={(e) => setStaffForm({ ...staffForm, email: e.target.value })} data-testid="staff-email" /></div>
            <div><Label>Password</Label><Input className="mt-1" type="text" value={staffForm.password} onChange={(e) => setStaffForm({ ...staffForm, password: e.target.value })} data-testid="staff-password" /></div>
            <Button className="w-full" disabled={staffBusy} onClick={createStaff} data-testid="staff-submit">{staffBusy ? "Creazione…" : "Crea profilo"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
